import type { SupabaseClient } from "@supabase/supabase-js";

import { readWebReleasePolicy, type ReleasePolicy } from "../update/release-policy";

// Release status read model for the Admin "Release health" panel. Combines two EXISTING backend
// pieces (no new migrations):
//   - the 'web' channel app_release_policy (latest / min-supported / force-update) — reused from
//     update/release-policy.ts.
//   - public.management_client_app_version_distribution — a SAFE aggregate (client_count per
//     channel/platform/app_version). It exposes NO individual identity: the raw client_app_status
//     table (actor ids, device ids, user agents) is never read here.
// Explicit safe selects only; never select("*").

export interface VersionDistributionRow {
  channel: string;
  platform: string;
  appVersion: string;
  clientCount: number;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
}

export interface ReleaseStatusData {
  policy: ReleasePolicy | null;
  distribution: VersionDistributionRow[];
}

const VERSION_DISTRIBUTION_COLUMNS =
  "channel, platform, app_version, client_count, first_seen_at, last_seen_at";

const DISTRIBUTION_LIMIT = 100;

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

// client_count is a bigint; supabase-js may hand it back as a number or a string. Coerce honestly.
function asCount(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return 0;
}

export function mapVersionDistributionRow(raw: Record<string, unknown>): VersionDistributionRow {
  return {
    channel: asString(raw.channel) ?? "web",
    platform: asString(raw.platform) ?? "web",
    appVersion: asString(raw.app_version) ?? "",
    clientCount: asCount(raw.client_count),
    firstSeenAt: asString(raw.first_seen_at),
    lastSeenAt: asString(raw.last_seen_at),
  };
}

/** Read the account's client version distribution (safe aggregate). Throws on read error. */
export async function fetchVersionDistribution(
  client: SupabaseClient,
  accountId: string,
  limit: number = DISTRIBUTION_LIMIT,
): Promise<VersionDistributionRow[]> {
  const { data, error } = await client
    .from("management_client_app_version_distribution")
    .select(VERSION_DISTRIBUTION_COLUMNS)
    .eq("account_id", accountId)
    .order("last_seen_at", { ascending: false })
    .limit(limit);
  if (error !== null) {
    throw new Error("Could not load release status.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((row) => mapVersionDistributionRow(row as Record<string, unknown>));
}

/**
 * Load the release policy + version distribution together for the panel. The distribution is the
 * panel's primary data (throws into the surface's error state on failure); the policy degrades to
 * null on its own read error so the panel can still show the deployed version + distribution.
 */
export async function fetchReleaseStatus(
  client: SupabaseClient,
  accountId: string,
): Promise<ReleaseStatusData> {
  const [policy, distribution] = await Promise.all([
    readWebReleasePolicy(client).catch(() => null),
    fetchVersionDistribution(client, accountId),
  ]);
  return { policy, distribution };
}
